import { WidgetEditor } from '@juvare/widget-ui/WidgetEditor';
import React from 'react';
import { FormConfigContext } from '../FormConfigContext';
import { defaultFormWidgets } from '../WidgetForm';
import { defaultGroupLabelProvider, defaultWidgetLabelProvider } from '../WidgetForm/Utils';
/**
 * Renders a form editor component that provides advanced widget editing capabilities.
 *
 * @template RootConfig - The type of the root configuration object.
 * @param {FormEditorProps<RootConfig>} props - The props for the FormEditor component.
 * @param {RootConfig} props.config - The root configuration for the form.
 * @param {Widget[]} [props.widgets] - Optional array of available widgets for the editor.
 * @param {WidgetEditorToolbarButton[]} [props.toolbarButtons] - Optional toolbar buttons for the widget editor.
 * @param {ValueProvider[]} [props.valueProviders] - Optional custom value providers.
 * @param {SubFormProvider[]} [props.subFormProviders] - Optional subform providers.
 * @param {Rule<any>[]} [props.rules] - Optional set of validation or dynamic behavior rules.
 * @param {GlobalWidgetConfig} [props.globalWidgetConfig] - Optional global widget configuration.
 * @param {Record<string, any>} [props.formParams] - Optional parameters passed to the form.
 * @param {(config: any) => void} props.onSave - Callback triggered when the form is saved.
 * @param {() => void} [props.onCancel] - Optional callback triggered on cancel.
 * @param {(changed: boolean, save: () => void) => void} [props.onChanged] - Optional callback triggered when the form changes.
 * @param {boolean} [props.initialPreviewState] - Optional flag to set the initial preview state.
 * @returns {React.ReactElement} The rendered FormEditor component.
 */
export function FormEditor(props) {
    var _a, _b;
    const context = React.useContext(FormConfigContext);
    const rootParams = React.useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        return ({
            showErrors: 'all',
            valueProviders: (_b = (_a = props.valueProviders) !== null && _a !== void 0 ? _a : context.valueProviders) !== null && _b !== void 0 ? _b : emptyValueProviders,
            subFormProviders: (_d = (_c = props.subFormProviders) !== null && _c !== void 0 ? _c : context.subFormProviders) !== null && _d !== void 0 ? _d : emptySubFormProviders,
            rules: (_f = (_e = props.rules) !== null && _e !== void 0 ? _e : context.rules) !== null && _f !== void 0 ? _f : emptyRules,
            globalWidgetConfig: (_h = (_g = props.globalWidgetConfig) !== null && _g !== void 0 ? _g : context.globalWidgetConfig) !== null && _h !== void 0 ? _h : emptyGlobalWidgetConfig,
            formParams: (_j = props.formParams) !== null && _j !== void 0 ? _j : context.formParams,
        });
    }, [
        props.valueProviders,
        props.subFormProviders,
        props.rules,
        props.globalWidgetConfig,
        props.formParams,
        context.valueProviders,
        context.subFormProviders,
        context.rules,
        context.globalWidgetConfig,
        context.formParams,
    ]);
    return (React.createElement(WidgetEditor, Object.assign({ rootParams: rootParams, widgets: (_b = (_a = props.widgets) !== null && _a !== void 0 ? _a : context.widgets) !== null && _b !== void 0 ? _b : defaultFormWidgets, groupLabelProvider: defaultGroupLabelProvider, widgetLabelProvider: defaultWidgetLabelProvider, toolbarButtons: props.toolbarButtons, formatConfigOnSave: true, maxUndoStackLength: 100, allowEditConfigJson: true }, props)));
}
const emptyValueProviders = [];
const emptySubFormProviders = [];
const emptyRules = [];
const emptyGlobalWidgetConfig = {};
